import { useCallback } from "react"
import { type ChipProps } from "@octopus-energy/coral/atoms"
import { getMapKeyOr } from "@oefr/utils/maps"
import { type FragmentOf, graphql, readFragment, type Scalar } from "@/graphql"
import { isAgreementTerminated } from "../utils/isAgreementTerminated"
import { useIsLeaveSupplierProcessAgreement } from "./useIsLeaveSupplierProcessAgreement"

const STATUS_COLOR_MAP = {
    ON_SUPPLY: "success500",
    AWAITING_SWITCH_IN_DECISION: "secondary500",
    INITIAL_METER_REGISTERED: "secondary500",
    NEW: "secondary500",
    PENDING_SITE_WORKS: "secondary500",
    PRE_APPLICATION: "secondary500",
    SWITCH_IN_APPLICATION_CONFIRMED: "secondary500",
    METER_INELIGIBLE: "error500",
    APPLICATION_REJECTED: "error500",
    WITHDRAWN: "error500",
    CLOSED: "error500",
    DUPLICATE: "error500",
    PENDING_TERMINATION: "warning500",
} as const satisfies Partial<
    Record<Scalar<"ActivationProcessStatus"> | "PENDING_TERMINATION", ChipProps["color"]>
>

const useGetAgreementStatusColor_AgreementFragment = graphql(
    `
        fragment useGetAgreementStatusColor_Agreement on AgreementType {
            status
            ...isAgreementTerminated_Agreement
            ...useIsLeaveSupplierProcessAgreement_Agreement
        }
    `,
    [
        isAgreementTerminated.fragments.agreement,
        useIsLeaveSupplierProcessAgreement.fragments.agreement,
    ]
)

type UseGetAgreementStatusColorProps = {
    accountNumber: string
}

type GetAgreementStatusColorProps = {
    agreement: FragmentOf<typeof useGetAgreementStatusColor_AgreementFragment>
}

export function useGetAgreementStatusColor({ accountNumber }: UseGetAgreementStatusColorProps) {
    const isLeaveSupplierProcessAgreement = useIsLeaveSupplierProcessAgreement({
        accountNumber,
    })

    return useCallback(
        (props: GetAgreementStatusColorProps) => {
            const agreement = readFragment(
                useGetAgreementStatusColor_AgreementFragment,
                props.agreement
            )

            if (isLeaveSupplierProcessAgreement({ agreement })) {
                return STATUS_COLOR_MAP.PENDING_TERMINATION
            }

            if (!agreement.status || isAgreementTerminated({ agreement })) {
                return STATUS_COLOR_MAP.CLOSED
            }

            return getMapKeyOr(agreement.status, STATUS_COLOR_MAP, "warning500")
        },
        [isLeaveSupplierProcessAgreement]
    )
}

useGetAgreementStatusColor.fragments = {
    agreement: useGetAgreementStatusColor_AgreementFragment,
}

useGetAgreementStatusColor.queryOptions = {
    ...useIsLeaveSupplierProcessAgreement.queryOptions,
}
